/**
 * PDF Text Extraction Service
 * 
 * This is a pluggable service for extracting text from PDF files.
 * Currently uses Apache Tika via backend API, but can be easily swapped
 * with other libraries (e.g., pdfjs-dist, pdf-parse) by changing the implementation.
 * 
 * Usage:
 *   const extractedText = await PdfTextExtractionService.extractText(pdfFile);
 * 
 * To swap implementation:
 *   1. Change the extractText method implementation
 *   2. Update the backend endpoint if needed
 *   3. No changes needed in calling code
 */

import { makeApiCall } from './ApiUtils';
import firebase from 'firebase/app';
import 'firebase/storage';

/**
 * PDF Text Extraction Service - Pluggable Implementation
 * 
 * This service can be easily swapped with other PDF extraction libraries:
 * - pdfjs-dist (Mozilla's PDF.js) for client-side extraction
 * - pdf-parse for Node.js
 * - pdf-lib for more advanced operations
 * 
 * Current implementation uses Apache Tika via backend API
 */
class PdfTextExtractionService {
  /**
   * Extract text from PDF file using Apache Tika
   * 
   * @param {File|Object} pdfFile - PDF file object or answer object with pdfFile property
   * @param {string} pdfPath - Optional: Firebase Storage path if file is already uploaded
   * @returns {Promise<{text: string, metadata: object, error: string|null}>}
   */
  static async extractText(pdfFile, pdfPath = null) {
    try {
      // Handle different input formats
      let file = null;
      let filePath = pdfPath;
      
      if (pdfFile instanceof File) {
        // Direct file object
        file = pdfFile;
      } else if (pdfFile && typeof pdfFile === 'object') {
        // Answer object with pdfFile property
        if (pdfFile.pdfFile && pdfFile.pdfFile.file) {
          file = pdfFile.pdfFile.file;
          filePath = pdfFile.pdfFile.pdfPath || pdfFile.pdfFile.url || null;
        } else if (pdfFile.pdfFile) {
          filePath = pdfFile.pdfFile.pdfPath || pdfFile.pdfFile.url || null;
        } else if (pdfFile.file) {
          file = pdfFile.file;
          filePath = pdfFile.pdfPath || pdfFile.url || null;
        }
      }
      
      // If we have a file path but no file object, try to get the file from Firebase Storage
      if (filePath && !file) {
        try {
          const downloadURL = await this.getPdfUrlFromPath(filePath);
          return await this.extractTextFromUrl(downloadURL);
        } catch (error) {
          console.error('Failed to get PDF from path:', error);
          throw new Error(`Failed to access PDF file: ${error.message}`);
        }
      }
      
      // If we have a file object, extract text directly
      if (file) {
        return await this.extractTextFromFile(file);
      }
      
      throw new Error('No valid PDF file or path provided');
    } catch (error) {
      console.error('PDF text extraction failed:', error);
      return {
        text: '',
        metadata: {},
        error: error.message || 'Failed to extract text from PDF',
        extractedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Extract text from PDF file using pdf-parse (backend API)
   * 
   * @param {File} file - PDF file object
   * @returns {Promise<{text: string, metadata: object, error: string|null}>}
   */
  static async extractTextFromFile(file) {
    try {
      // Validate file type
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        throw new Error('Invalid file type. Expected PDF file.');
      }

      // Convert file to base64 for API transmission
      const base64Data = await this.fileToBase64(file);
      
      // Call backend API for text extraction using pdf-parse
      const result = await makeApiCall('/api/extract-pdf-text', {
        pdfData: base64Data,
        fileName: file.name,
        fileSize: file.size
      });

      if (!result) {
        throw new Error('No response from PDF extraction API');
      }

      return {
        text: result.text || '',
        metadata: result.metadata || {},
        error: result.error || null,
        extractedAt: new Date().toISOString(),
        extractionMethod: result.extractionMethod || 'pdf-parse'
      };
    } catch (error) {
      console.error('PDF text extraction from file failed:', error);
      return {
        text: '',
        metadata: {},
        error: error.message || 'Failed to extract text from PDF file',
        extractedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Extract text from PDF URL using pdf-parse (backend API)
   * 
   * @param {string} pdfUrl - URL to PDF file
   * @returns {Promise<{text: string, metadata: object, error: string|null}>}
   */
  static async extractTextFromUrl(pdfUrl) {
    try {
      if (!pdfUrl || typeof pdfUrl !== 'string') {
        throw new Error('Invalid PDF URL provided');
      }

      // Call backend API for text extraction using pdf-parse
      const result = await makeApiCall('/api/extract-pdf-text', {
        pdfUrl: pdfUrl
      });

      if (!result) {
        throw new Error('No response from PDF extraction API');
      }

      return {
        text: result.text || '',
        metadata: result.metadata || {},
        error: result.error || null,
        extractedAt: new Date().toISOString(),
        extractionMethod: result.extractionMethod || 'pdf-parse'
      };
    } catch (error) {
      console.error('PDF text extraction from URL failed:', error);
      return {
        text: '',
        metadata: {},
        error: error.message || 'Failed to extract text from PDF URL',
        extractedAt: new Date().toISOString()
      };
    }
  }

  /**
   * Get PDF download URL from Firebase Storage path
   * 
   * @param {string} pdfPath - Firebase Storage path (e.g., "examCode/questionId/filename.pdf")
   * @returns {Promise<string>} Download URL
   */
  static async getPdfUrlFromPath(pdfPath) {
    try {
      // Add 'submissions/' prefix if not present
      const fullPath = pdfPath.startsWith('submissions/') ? pdfPath : `submissions/${pdfPath}`;
      const storageRef = firebase.storage().ref(fullPath);
      return await storageRef.getDownloadURL();
    } catch (error) {
      console.error('Failed to get PDF URL from path:', error);
      throw new Error(`Failed to get PDF download URL: ${error.message}`);
    }
  }

  /**
   * Convert File object to base64 string
   * 
   * @param {File} file - File object
   * @returns {Promise<string>} Base64 encoded string
   */
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix (data:application/pdf;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Combine extracted PDF text with existing answer text
   * 
   * @param {string} existingText - Existing text content from answer
   * @param {string} pdfText - Extracted text from PDF
   * @returns {string} Combined text
   */
  static combineTextWithPdf(existingText, pdfText) {
    if (!pdfText || !pdfText.trim()) {
      return existingText || '';
    }
    
    const pdfTextSection = `\n\n--- Content from PDF attachment ---\n${pdfText.trim()}\n--- End of PDF content ---`;
    
    if (existingText && existingText.trim()) {
      return `${existingText.trim()}${pdfTextSection}`;
    }
    
    return pdfText.trim();
  }
}

export default PdfTextExtractionService;

